import { useEffect, useState } from 'react';
import { Confirm, SaveView, sortOf, useAdmin, useFilters } from '../ui/Shell';
import { Block, DateRange, ListMore, Table, type Row } from '../ui/Block';
import { post } from '../lib/api';
import { describeErr, hhmmss } from '../lib/hooks';
import { countOf, qs, useDebounced, usePagedList, type Paged } from '../lib/list';
import { When, redactEmails, type ProviderEvent } from './billingShared';

function useSearch(value: string, write: (v: string) => void): [string, (v: string) => void] {
  const [q, setQ] = useState(value);
  const dq = useDebounced(q.trim(), 300);
  useEffect(() => { if (dq !== value) write(dq); }, [dq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setQ((c) => (c.trim() === value ? c : value)); }, [value]);
  return [q, setQ];
}

export function BillingEvents({ mobile, paused }: { mobile: boolean; paused: boolean }) {
  const ctx = useAdmin();
  const [f, setF] = useFilters({ tab: '', failed: '', q: '', from: '', to: '', sort: '', dir: '' });
  const set = (p: Partial<typeof f>) => setF({ ...p, tab: 'events' });
  const s = sortOf(f, set, { key: 'received', dir: 'desc' });
  const [q, setQ] = useSearch(f.q, (v) => set({ q: v }));
  const failed = f.failed === '1';
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const path = `/admin/billing/events${qs({ failed: failed ? 1 : '', q: f.q, from: f.from, to: f.to, sort: f.sort, dir: f.sort ? s.sort.dir : '' })}`;
  const l = usePagedList<ProviderEvent, Paged & { events: ProviderEvent[] }>(ctx.env, path, 'events', 30, paused, (e) => `${e.provider}:${e.id}`);
  const events = l.rows;
  const filtered = !!(f.q || f.from || f.to);

  const retry = (e: ProviderEvent) => ctx.openModal(
    <Confirm env={ctx.env} mobile={mobile} app={`${e.provider} · ${e.id}`} title={`Retry ${e.type}`} confirmLabel="Retry" deskOnly onClose={ctx.closeModal}
      body={`Replays the stored event through the provider handler. It failed with: ${e.error ?? 'unknown'}. Handlers are idempotent on the event id, so a replay that already landed is a no-op.`}
      onConfirm={async () => { await post(ctx.env, `/admin/billing/events/${encodeURIComponent(e.id)}/retry`); ctx.toast(`Retried ${e.type}`, e.id); l.reload(); ctx.reloadAttention(); }} />,
  );

  const rows: Row[] = events.map((e) => ({
    key: `${e.provider}:${e.id}`,
    expanded: !!open[e.id],
    meta: JSON.stringify(redactEmails({ ...e, payload: e.payload ?? '(the events route carries no payload)' }), null, 2),
    onClick: () => setOpen((o) => ({ ...o, [e.id]: !o[e.id] })),
    cells: [
      { t: e.type, mono: true, fw: 600, sub: <span className="mono">{e.provider} · {e.id}</span> },
      { node: <When iso={e.receivedAt} /> },
      e.error ? { pill: { label: 'FAILED', tone: 'bad' }, sub: e.error } : e.processedAt ? { pill: { label: 'PROCESSED', tone: 'ok' }, sub: <When iso={e.processedAt} /> } : { pill: { label: 'PENDING', tone: 'mute' } },
      { acts: e.error ? [{ label: 'Retry', disabled: mobile, onClick: () => retry(e) }] : [] },
    ],
  }));

  return (
    <Block title="Provider events" count={countOf(l)} asOf={hhmmss(l.at)} stale={l.ageS > 90} loading={l.loading && !l.data}
      error={describeErr(l.error) ? { ...describeErr(l.error)!, retry: l.reload } : null}
      sub="Webhooks as received, newest first. Click a row for the raw record; email fields are redacted here."
      chips={[{ label: 'Failed only', on: failed, onClick: () => set({ failed: failed ? '' : '1' }) }]}
      search={{ value: q, placeholder: 'Event type or id', onChange: setQ }}
      extra={<><DateRange label="Received" from={f.from} to={f.to} onChange={(from, to) => set({ from, to })} /><SaveView /></>}
      empty={l.data && events.length === 0 ? (filtered ? 'No events match.' : failed ? 'No failed events.' : 'No provider events yet.') : undefined} emptyGood={failed && !filtered}
      foot={<ListMore list={l} noun="events" />}>
      <Table mobile={mobile} minWidth={800} rows={rows} sort={s.sort} onSort={s.onSort} cols={[{ label: 'EVENT', width: 'minmax(260px,2fr)', sort: 'type', sortAsc: true }, { label: 'RECEIVED', width: '120px', sort: 'received' }, { label: 'STATE', width: 'minmax(200px,1.5fr)', sort: 'state', sortAsc: true }, { label: '', width: '90px', align: 'r' }]} />
    </Block>
  );
}
